// src/pages/customer-panel/invoices/InvoiceDetailPage.js
// Invoice Detail Page

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axiosInstance from '../../../utils/axiosInstance';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '16px',
            color: '#1e293b',
            '::placeholder': { color: '#94a3b8' },
        },
        invalid: { color: '#ef4444', iconColor: '#ef4444' },
    },
};
const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    paid: { color: '#10b981', bg: '#d1fae5', label: 'Paid' },
    unpaid: { color: '#f59e0b', bg: '#fef3c7', label: 'Unpaid' },
    pending: { color: '#f59e0b', bg: '#fef3c7', label: 'Pending' },
    overdue: { color: '#ef4444', bg: '#fee2e2', label: 'Overdue' },
    draft: { color: '#64748b', bg: '#f1f5f9', label: 'Draft' },
    void: { color: '#6b7280', bg: '#f3f4f6', label: 'Void' },
};

// --- Pay By Card Section (Customer) ---
function PayByCardSection({ invoiceId, onPay }) {
    const [methods, setMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(true);
    const [selectedMethodId, setSelectedMethodId] = useState(null);
    const [isPaying, setIsPaying] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        axiosInstance.get('/payment-methods')
            .then((res) => {
                const list = res.data || res.data?.payment_methods || [];
                setMethods(list);
                const defaultMethod = list.find((m) => m.is_default);
                if (defaultMethod) setSelectedMethodId(defaultMethod.id);
                else if (list.length > 0) setSelectedMethodId(list[0].id);
            })
            .catch(() => setMethods([]))
            .finally(() => setLoadingMethods(false));
    }, []);

    const handlePay = async () => {
        if (!selectedMethodId) return;
        setIsPaying(true);
        try {
            await onPay(invoiceId, selectedMethodId);
        } finally {
            setIsPaying(false);
        }
    };

    return (
        <Paper sx={{
            mt: 4, p: 3, borderRadius: '12px',
            border: '2px solid', borderColor: GREEN_ACCENT,
        }}>
            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                Complete Payment
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                Select a card below to pay the outstanding amount.
            </Typography>

            <Box sx={{ mt: 2 }}>
                {loadingMethods && (
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center', py: 2 }}>
                        Loading your cards...
                    </Typography>
                )}
                {!loadingMethods && methods.length === 0 && (
                    <Box sx={{
                        textAlign: 'center', p: 3, border: '2px dashed #e2e8f0', borderRadius: '10px',
                    }}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                            You have no saved payment methods.
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: GREEN_ACCENT, fontWeight: 600, mt: 0.5, cursor: 'pointer' }}>
                            Add a card to continue
                        </Typography>
                    </Box>
                )}
                {methods.map((method) => (
                    <Box
                        key={method.id}
                        onClick={() => setSelectedMethodId(method.id)}
                        sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            p: 1.5, mb: 1, borderRadius: '10px', cursor: 'pointer',
                            border: '2px solid',
                            borderColor: selectedMethodId === method.id ? GREEN_ACCENT : '#e2e8f0',
                            bgcolor: selectedMethodId === method.id ? '#f0fdf4' : 'transparent',
                            transition: 'all 0.15s',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CreditCardIcon sx={{ color: selectedMethodId === method.id ? GREEN_ACCENT : '#94a3b8', fontSize: 20 }} />
                            <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                                <strong style={{ textTransform: 'capitalize' }}>{method.card_brand}</strong>
                                {' '}&bull;&bull;&bull;&bull; {method.last4}
                            </Typography>
                        </Box>
                        {method.is_default && (
                            <Chip label="Default" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#64748b' }} />
                        )}
                    </Box>
                ))}
            </Box>

            <Button
                fullWidth
                variant="contained"
                onClick={handlePay}
                disabled={isPaying || !selectedMethodId || loadingMethods || methods.length === 0}
                startIcon={isPaying ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
                sx={{
                    mt: 2.5, fontFamily, fontWeight: 700, bgcolor: GREEN_ACCENT,
                    borderRadius: '10px', textTransform: 'none', py: 1.5, fontSize: '1rem',
                    '&:hover': { bgcolor: '#059669' },
                }}
            >
                {isPaying ? 'Processing...' : 'Pay Now'}
            </Button>
            <Typography
                sx={{
                    fontFamily,
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    textAlign: 'center',
                    mt: 1.5
                }}
                >
                Need to change cards?{" "}
                <span
                    onClick={() => navigate("/customer-panel/payment-methods")}
                    style={{
                    color: GREEN_ACCENT,
                    fontWeight: 600,
                    cursor: "pointer"
                    }}
                >
                    Manage Payment Methods
                </span>
            </Typography>
        </Paper>
    );
}

// --- One-Time Card Payment Form (inside <Elements>) ---
function OneTimeCardForm({ clientSecret, invoiceId, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setProcessing(true);
        setError(null);

        const cardElement = elements.getElement(CardElement);
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
        });

        if (stripeError) {
            setError(stripeError.message);
            setProcessing(false);
            return;
        }

        if (paymentIntent?.status === 'succeeded') {
            try {
                await axiosInstance.post(`/invoices/${invoiceId}/confirm-payment`, {
                    payment_intent_id: paymentIntent.id,
                });
                onSuccess('Payment successful!');
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to confirm payment.');
            }
        } else {
            setError(`Payment not completed. Status: ${paymentIntent?.status}`);
        }
        setProcessing(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 2 }}>
                Enter your card details below. Payment is processed securely via Stripe.
            </Typography>
            <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: '#fff', mb: 2 }}>
                <CardElement options={CARD_ELEMENT_OPTIONS} />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily }}>{error}</Alert>}
            <Button
                type="submit" fullWidth variant="contained"
                disabled={processing || !stripe}
                startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
                sx={{ fontFamily, fontWeight: 700, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', py: 1.5, '&:hover': { bgcolor: '#059669' } }}
            >
                {processing ? 'Processing...' : 'Pay with Card'}
            </Button>
        </form>
    );
}

// --- One-Time Card Payment Section ---
function OneTimePaymentSection({ invoiceId, amount, onSuccess }) {
    const [clientSecret, setClientSecret] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState(null);

    const handleStartPayment = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post(`/invoices/${invoiceId}/create-payment-intent`);
            setClientSecret(res.data.client_secret);
            setShowForm(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initialize payment.');
        } finally {
            setLoading(false);
        }
    };

    if (!showForm) {
        return (
            <Box sx={{ mt: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily }}>{error}</Alert>}
                <Button
                    fullWidth variant="outlined"
                    onClick={handleStartPayment}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <CreditCardIcon />}
                    sx={{ fontFamily, fontWeight: 600, borderRadius: '10px', textTransform: 'none', borderColor: '#3b82f6', color: '#3b82f6', py: 1.2, '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' } }}
                >
                    {loading ? 'Initializing...' : `Pay $${parseFloat(amount).toFixed(2)} with New Card`}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
                <OneTimeCardForm clientSecret={clientSecret} invoiceId={invoiceId} onSuccess={onSuccess} />
            </Elements>
        </Box>
    );
}

export default function InvoiceDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // User role
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team?.toLowerCase() || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'owner';
    const isCustomer = userRole === 'customer';

    const fetchInvoice = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/invoices/${id}`);
            setInvoice(response.data?.invoice || response.data?.data || response.data);
        } catch (err) {
            console.error('Failed to fetch invoice:', err);
            setError(err.response?.data?.message || 'Failed to load invoice details.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        document.title = 'Invoice Detail | Customer Panel';
        fetchInvoice();
    }, [fetchInvoice]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch {
            return '--';
        }
    };

    const formatCurrency = (amount) => {
        if (amount == null) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const handleDownloadPDF = async () => {
        try {
            const response = await axiosInstance.get(`/invoices/${id}/pdf`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${invoice?.invoice_number || id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download PDF:', err);
            setSnackbar({ open: true, message: 'Failed to download PDF.', severity: 'error' });
        }
    };

    const handleCardPayment = async (invoiceId, paymentMethodId) => {
        try {
            await axiosInstance.post(`/invoices/${invoiceId}/pay`, {
                type: 'card',
                payment_method_id: paymentMethodId,
            });
            setSnackbar({ open: true, message: 'Payment successful!', severity: 'success' });
            fetchInvoice();
        } catch (err) {
            console.error('Card payment failed:', err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Payment failed.', severity: 'error' });
        }
    };

    const handleCashPayment = async () => {
        if (!window.confirm('Are you sure you want to mark this invoice as paid by cash? This action cannot be undone.')) return;
        setIsProcessing(true);
        try {
            await axiosInstance.post(`/invoices/${invoice?.id || invoice?._id || id}/pay`, { type: 'cash' });
            setSnackbar({ open: true, message: 'Invoice marked as paid!', severity: 'success' });
            fetchInvoice();
        } catch (err) {
            console.error('Cash payment failed:', err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to mark as paid.', severity: 'error' });
        } finally {
            setIsProcessing(false);
        }
    };

    const getStatusChip = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
        return (
            <Chip
                label={config.label}
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    bgcolor: config.bg,
                    color: config.color,
                }}
            />
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: GREEN_ACCENT }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/invoices')}
                    sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                >
                    Back to Invoices
                </Button>
            </Box>
        );
    }

    const lineItems = invoice?.line_items || invoice?.lineItems || invoice?.items || [];
    const subtotal = invoice?.subtotal ?? lineItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.price || item.rate || 0)), 0);
    const tax = invoice?.tax ?? invoice?.tax_amount ?? 0;
    const total = invoice?.total ?? invoice?.amount ?? (subtotal + tax);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
            {/* Top Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/invoices')}
                    sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                >
                    Back to Invoices
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton onClick={fetchInvoice} sx={{ color: '#64748b' }}>
                        <RefreshIcon />
                    </IconButton>
                    <Button
                        startIcon={<PrintIcon />}
                        variant="outlined"
                        onClick={() => window.print()}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            borderColor: '#e2e8f0',
                            color: '#64748b',
                            borderRadius: '10px',
                            textTransform: 'none',
                        }}
                    >
                        Print
                    </Button>
                    <Button
                        startIcon={<DownloadIcon />}
                        variant="contained"
                        onClick={handleDownloadPDF}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: GREEN_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Download PDF
                    </Button>
                </Box>
            </Box>

            {/* Invoice */}
            <Paper
                sx={{
                    p: { xs: 3, md: 5 },
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
            >
                {/* Invoice Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <ReceiptIcon sx={{ color: GREEN_ACCENT, fontSize: 32 }} />
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: '#1e293b' }}>
                                INVOICE
                            </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: GREEN_ACCENT }}>
                            {invoice?.invoice_number || invoice?.invoiceNumber || `INV-${id}`}
                        </Typography>
                    </Box>
                    {getStatusChip(invoice?.status)}
                </Box>

                {/* Dates */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                            Invoice Date
                        </Typography>
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                            {formatDate(invoice?.created_on || invoice?.created_at || invoice?.date)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                            Due Date
                        </Typography>
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                            {formatDate(invoice?.due_on || invoice?.due_date || invoice?.dueDate)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                            Payment Terms
                        </Typography>
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                            {invoice?.payment_terms || invoice?.paymentTerms || 'Net 30'}
                        </Typography>
                    </Grid>
                </Grid>

                {/* Linked Appointment */}
                {invoice?.appointment && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
                        <Typography variant="subtitle2" sx={{ fontFamily, fontWeight: 700, color: '#166534', mb: 0.5 }}>
                            Linked Appointment
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily, color: '#15803d' }}>
                            {invoice.appointment.title}
                            {invoice.appointment.appointment_date && ` - ${new Date(invoice.appointment.appointment_date).toLocaleDateString()}`}
                            {invoice.appointment.status && (
                                <Chip label={invoice.appointment.status} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                            )}
                        </Typography>
                    </Paper>
                )}

                <Divider sx={{ mb: 4 }} />

                {/* From / To */}
                <Grid container spacing={4} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                            From
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                                {invoice?.from_name || invoice?.fromName || invoice?.company_name || 'Company Name'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                                {invoice?.from_email || invoice?.fromEmail || ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                                {invoice?.from_address || invoice?.fromAddress || ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                                {invoice?.from_phone || invoice?.fromPhone || ''}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                            Bill To
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                                {invoice?.customer_name || invoice?.customerName || invoice?.to_name || invoice?.toName || 'Customer Name'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                                {invoice?.customer_email || invoice?.customerEmail || invoice?.to_email || ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                                {invoice?.customer_address || invoice?.customerAddress || invoice?.to_address || ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                                {invoice?.customer_phone || invoice?.customerPhone || invoice?.to_phone || ''}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Line Items Table */}
                <TableContainer sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'grey.200' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Item', 'Description', 'Qty', 'Rate', 'Amount'].map((header) => (
                                    <TableCell
                                        key={header}
                                        align={['Qty', 'Rate', 'Amount'].includes(header) ? 'right' : 'left'}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#64748b',
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lineItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No line items</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                lineItems.map((item, index) => {
                                    const qty = item.quantity || item.qty || 1;
                                    const rate = item.price || item.rate || item.unit_price || 0;
                                    const amount = item.amount || item.total || qty * rate;
                                    return (
                                        <TableRow key={item.id || index}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                {item.name || item.item || `Item ${index + 1}`}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                {item.description || '--'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily, color: '#1e293b' }}>
                                                {qty}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily, color: '#1e293b' }}>
                                                {formatCurrency(rate)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(amount)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Totals */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{ minWidth: 280 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography sx={{ fontFamily, color: '#64748b' }}>Subtotal</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formatCurrency(subtotal)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography sx={{ fontFamily, color: '#64748b' }}>Tax</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formatCurrency(tax)}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                                Total
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, color: GREEN_ACCENT, fontSize: '1.1rem' }}>
                                {formatCurrency(total)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Notes */}
                {(invoice?.notes || invoice?.memo || invoice?.item_description) && (
                    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'grey.100' }}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                            {invoice?.item_description ? 'Description' : 'Notes'}
                        </Typography>
                        <Box sx={{ mt: 1, bgcolor: '#f8fafc', borderRadius: '10px', p: 2 }}>
                            <Typography variant="body2" sx={{ fontFamily, color: '#64748b', whiteSpace: 'pre-wrap' }}>
                                {invoice?.item_description || invoice?.notes || invoice?.memo}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Payment Type Info */}
                {invoice?.payment_type && (
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                            Payment Method:
                        </Typography>
                        <Chip
                            label={invoice.payment_type === 'card' ? 'Credit Card' : 'Cash / In-Person'}
                            size="small"
                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', bgcolor: '#f1f5f9', color: '#475569', textTransform: 'capitalize' }}
                        />
                    </Box>
                )}
            </Paper>

            {/* Payment Section - shown for unpaid invoices */}
            {(invoice?.status === 'unpaid' || invoice?.status === 'pending') && (
                <Paper sx={{ mt: 3, p: 3, borderRadius: '12px', border: '2px solid', borderColor: GREEN_ACCENT }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 0.5 }}>
                        Payment Options
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
                        Amount due: <strong>${parseFloat(invoice?.amount || 0).toFixed(2)}</strong>
                    </Typography>

                    {/* Option 1: Pay with saved card (customer only) */}
                    {isCustomer && invoice?.payment_type === 'card' && (
                        <Box sx={{ mb: 2 }}>
                            <PayByCardSection
                                invoiceId={invoice.id || invoice._id || id}
                                onPay={handleCardPayment}
                            />
                        </Box>
                    )}

                    {/* Option 2: Pay with one-time new card (any role) */}
                    <OneTimePaymentSection
                        invoiceId={invoice.id || invoice._id || id}
                        amount={invoice?.amount || 0}
                        onSuccess={(msg) => {
                            setSnackbar({ open: true, message: msg, severity: 'success' });
                            fetchInvoice();
                        }}
                    />

                    {/* Option 3: Mark as cash paid (admin/employee only) */}
                    {(isAdmin || userRole === 'employee') && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ my: 2 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>OR</Typography>
                            </Divider>
                            <Button
                                fullWidth variant="outlined"
                                onClick={handleCashPayment}
                                disabled={isProcessing}
                                startIcon={isProcessing ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                                sx={{ fontFamily, fontWeight: 600, borderRadius: '10px', textTransform: 'none', borderColor: '#f59e0b', color: '#d97706', py: 1.2, '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' } }}
                            >
                                {isProcessing ? 'Processing...' : 'Mark as Paid (Cash)'}
                            </Button>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Paid Status Display */}
            {invoice?.status === 'paid' && (
                <Paper sx={{
                    mt: 3, p: 3, borderRadius: '12px', textAlign: 'center',
                    border: '2px solid #10b981', bgcolor: '#f0fdf4',
                }}>
                    <CheckCircleIcon sx={{ color: GREEN_ACCENT, fontSize: 32, mb: 1 }} />
                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#065f46', fontSize: '0.95rem' }}>
                        Paid on {formatDate(invoice.paid_on || invoice.paid_at || invoice.paidAt)} via{' '}
                        {invoice.payment_type === 'card' && invoice.payment_method_details ? (
                            <>
                                <strong style={{ textTransform: 'capitalize' }}>{invoice.payment_method_details.card_brand}</strong>
                                {' '}ending in {invoice.payment_method_details.last4}
                            </>
                        ) : (
                            <span style={{ textTransform: 'capitalize' }}>{invoice.payment_type || 'payment'}</span>
                        )}
                    </Typography>
                </Paper>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
